import type { Option } from "@tento-lona/sheets";
import { Tracing } from "@tento-core/tracing";
import { LiveData } from "@tento-core/livedata";
import { TimezoneRegion, Tzname } from "@tento-chrono";
import { DependencyAspect } from "@tento-core/dependency-graph";
import { LedgerKeys } from "@tento-lona/sheets/internal-sdk";
import type { LedgerBumper } from "@tento-lona/sheets/internal-sdk";
import type { ClientLifecycle } from "../client/handle-errors.ts";
import type { PreferencesStorage } from "./preferences-storage.ts";
import { ZoneLoad } from "./zone-load.ts";
import { type Actor, type InvalidationSink, TimezoneChanged } from "../invalidation.ts";

const STORAGE_KEY = "lona-preferred-timezone";

/**
 * The user's preferred timezone, stored locally
 * (`client.preferences.timezone()`). `peek()` reads it (`null`: the
 * runtime's own zone), `live` observes it, and `set(tz, actor)` is its one
 * writer: it stores the zone and announces `TimezoneChanged` stamped with the
 * writer.
 */
export class TimezonePreferences {
  readonly #timezone: LiveData<Option<Tzname>>;

  readonly #storage: PreferencesStorage;
  readonly #invalidation: InvalidationSink;
  /** Row-identity ledger bumper (L2); late-bound — see `attachLedger`. */
  #ledger: Option<LedgerBumper>;
  /** The owning client's liveness: `set` checks it first. */
  readonly #lifecycle: ClientLifecycle;
  /**
   * The stored preference when it is no zone the runtime knows
   * (`Tzname.parseOpt` refused it: `America/Atlantis`), until `set` replaces
   * it: `peek()` reads `null` and the view anchors on the runtime's zone,
   * which `loadEffectiveRegion` reports as a fallback (`unknown-name`).
   */
  #unknownStored: Option<string>;
  /** The fallback the last `loadEffectiveRegion` took (`peekZoneFallback`, `liveZoneFallback`). */
  readonly #zoneFallback = new LiveData<Option<TimezonePreferences.ZoneFallback>>(null);

  /** `lifecycle`: the owning client's (a standalone face: `new LonaSdk.Client.Lifecycle()`). */
  constructor(
    storage: PreferencesStorage,
    invalidation: InvalidationSink,
    ledger: Option<LedgerBumper>,
    lifecycle: ClientLifecycle,
  ) {
    this.#storage = storage;
    this.#invalidation = invalidation;
    this.#ledger = ledger;
    this.#lifecycle = lifecycle;
    const stored = storage.getItem(STORAGE_KEY) ?? "";
    const timezone = Tzname.parseOpt(stored) ?? null;
    this.#timezone = new LiveData<Option<Tzname>>(timezone);
    this.#unknownStored = timezone == null && stored !== "" ? stored : null;
  }

  /** The stored preference (`null`: the runtime's own zone), read synchronously. */
  peek(): Option<Tzname> {
    return this.#timezone.get();
  }

  /** The stored preference as a read-only live value, to observe: `set(tz, actor)` writes it. */
  get live(): LiveData.Readable<Option<Tzname>> {
    return this.#timezone;
  }

  /** Late-bind the row-identity ledger (attached once the accessor exists). */
  attachLedger(ledger: LedgerBumper): void {
    this.#ledger = ledger;
  }

  effectiveTimezoneName(): Tzname {
    return this.#timezone.get() ?? this.runtimeTimezoneName();
  }

  /**
   * The effective zone (`effectiveTimezoneName`), loaded through the SDK's
   * zone loader (`ZoneLoad`): a stored link (`US/Pacific`) is its canonical
   * zone; a name that does not load falls back to the runtime's zone, then
   * UTC, traced (`tz/zone-fallback`) and kept for `peekZoneFallback`
   * (`not-loaded`). A stored preference that is no zone at all
   * (`unknown-name`: the runtime does not know the name) is such a fallback
   * too: the view anchors on the runtime's zone in its place, traced and
   * kept the same way. So is a runtime that reports no zone it knows, with
   * no stored preference (`unknown-name`, `requested` the runtime's report,
   * `Etc/Unknown` when it reports none): the view anchors on UTC in its
   * place. Unless the preference changed while it loaded: that anchor is
   * stale, and the newer one's outcome stands.
   */
  async loadEffectiveRegion(): Promise<TimezoneRegion> {
    const name = this.effectiveTimezoneName();
    const unknownStored = this.#unknownStored;
    const unknown = unknownStored ?? (this.#timezone.get() == null ? unknownRuntimeZone() : null);
    // `null`: the viewer's fallback is this client's to keep (`peekZoneFallback`, `client.zones`), not every listener's.
    const resolved = await ZoneLoad.resolve(name, this.runtimeTimezoneName(), null);
    const zone = resolved.zone;
    if (String(this.effectiveTimezoneName()) !== String(name) || this.#unknownStored !== unknownStored) return zone;
    let fallback: Option<TimezonePreferences.ZoneFallback> = null;
    if (unknown != null) {
      fallback = { requested: unknown, used: String(zone.fullname), reason: "unknown-name" };
      Tracing.log("tz/zone-fallback", { name: unknown, fallback: fallback.used, reason: fallback.reason });
    } else if (resolved.fallback != null) {
      fallback = { requested: resolved.fallback.requested, used: String(resolved.fallback.used), reason: "not-loaded" };
    }
    const was = this.#zoneFallback.get();
    // A listener hears a change, not every anchor: the same fallback again is no news.
    if (was?.requested !== fallback?.requested || was?.used !== fallback?.used || was?.reason !== fallback?.reason) {
      this.#zoneFallback.update(fallback);
    }
    return zone;
  }

  /**
   * The fallback the last `loadEffectiveRegion` took (every view's anchor,
   * `AppWiring.anchorToday`, loads through it): the effective zone did not
   * load, and times show in `used` (the runtime's zone, or UTC), not the
   * zone the viewer has. `null`: it loaded, or none was loaded yet. A host
   * tells the viewer, as it reports any failure it tolerates.
   */
  peekZoneFallback(): Option<TimezonePreferences.ZoneFallback> {
    return this.#zoneFallback.get();
  }

  /**
   * `peekZoneFallback` as a read-only live value, for a host to observe
   * instead of peeking after every anchor: it changes when an anchor takes a
   * different fallback, or none after one.
   */
  get liveZoneFallback(): LiveData.Readable<Option<TimezonePreferences.ZoneFallback>> {
    return this.#zoneFallback;
  }

  /** The runtime's own IANA zone (`UTC` when it reports none it knows: `loadEffectiveRegion` tells that as a fallback). */
  runtimeTimezoneName(): Tzname {
    return Tzname.parseOpt(runtimeZoneReport()) ?? Tzname.UTC;
  }

  /**
   * Store the preferred timezone (`null`: follow the runtime's own zone)
   * and announce `TimezoneChanged` stamped with `actor`, the writer
   * (`Actor.EXTERNAL` for a caller with no echo of its own to recognise).
   */
  set(timezone: Option<Tzname>, actor: Actor): void {
    this.#lifecycle.assertLive("preferences.timezone.set");
    this.#unknownStored = null;
    if (timezone) {
      this.#storage.setItem(STORAGE_KEY, timezone);
    } else {
      this.#storage.removeItem(STORAGE_KEY);
    }
    this.#timezone.update(timezone);
    // LR1: bump the primary-timezone node (dark in L2).
    this.#ledger?.bump(LedgerKeys.TZ, DependencyAspect.VALUES, "tz");
    this.#invalidation.emit(new TimezoneChanged(actor));
  }

  isUsingBrowserDefault(): boolean {
    const preference = this.#timezone.get();
    return !preference || preference === this.runtimeTimezoneName();
  }
}

/** The zone the runtime reports (`Intl`), as it reports it: `undefined` or `""` when it has none to report. */
function runtimeZoneReport(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone ?? "";
}

/**
 * What the runtime reported when it is no zone it knows (ICU's
 * `Etc/Unknown`, which `Intl` itself refuses, or no report at all, which
 * reads as `Etc/Unknown`); `null` when it is one.
 */
function unknownRuntimeZone(): Option<string> {
  const reported = runtimeZoneReport();
  if (reported !== "" && Tzname.parseOpt(reported) != null) return null;
  return reported === "" ? "Etc/Unknown" : reported;
}

/** Types that go with `TimezonePreferences`. */
export namespace TimezonePreferences {
  /** A fallback `loadEffectiveRegion` took (`peekZoneFallback`): the zone name that did not load, the zone used, and why. */
  export interface ZoneFallback {
    /** The zone name that did not load (the stored preference, or the runtime's zone, `Etc/Unknown` when the runtime reported none). */
    readonly requested: string;
    /** The zone name the view anchors on instead: the runtime's zone, or `UTC`. */
    readonly used: string;
    /** Why: the runtime knows no zone by that name (`unknown-name`), or the zone did not load (`not-loaded`). */
    readonly reason: ZoneFallback.Reason;
  }

  /** Types that go with `ZoneFallback`. */
  export namespace ZoneFallback {
    /** `unknown-name`: a stored preference the runtime knows no zone by, or a runtime that reports no zone it knows; `not-loaded`: a zone name that did not load. */
    export type Reason = "unknown-name" | "not-loaded";
  }
}
